import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createWorker } from 'tesseract.js';
import '../styles/ItemScanner.css';
import BarcodeScannerInline from './BarcodeScannerInline';
import HelpDisclosure from './HelpDisclosure';

const scannerHelp = [
  {
    question: "Which scan mode should I use?",
    answer: "Use Receipt for printed receipts, Groceries for a photo of loose items, and Barcode for packaged products with a barcode."
  },
  {
    question: "What makes scanning more accurate?",
    answer: "Use bright light, keep the text or product centered, avoid blur, and crop out background clutter when possible."
  },
  {
    question: "What happens after scanning?",
    answer: "You review each detected item, edit names or quantities, choose pantry or fridge, then add everything to pantry."
  }
];

const scannerReviewHelp = [
  {
    question: "Can I edit detected items?",
    answer: "Yes. Change the item name, quantity, expiry date, and storage location before adding the items."
  },
  {
    question: "What if the scanner missed something?",
    answer: "Go back and try a clearer image, or use Add Manually from the scan screen."
  },
  {
    question: "Will all items be saved?",
    answer: "Only the items shown in the preview are added when you press Add all items."
  }
];

const ItemScanner = ({ onClose, onProductsExtracted }) => {
  const [activeTab, setActiveTab] = useState('receipt'); // 'receipt', 'groceries', or 'barcode'
  const [isScanning, setIsScanning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [products, setProducts] = useState([]);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [error, setError] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [extractedOcrText, setExtractedOcrText] = useState(''); // For debugging OCR
  
  // Camera and image states
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const productNameInputRefs = useRef([]);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Ensure video displays when stream is set and camera is shown
  useEffect(() => {
    if (stream && videoRef.current && showCamera) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (videoRef.current && stream) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(err => {
            console.error('Video play error:', err);
          });
          // Force visibility and styling
          videoRef.current.style.display = 'block';
          videoRef.current.style.width = '100%';
          videoRef.current.style.height = '100%';
          videoRef.current.style.minHeight = '400px';
          videoRef.current.style.objectFit = 'cover';
          videoRef.current.style.background = '#000';
          console.log('[OK] Video stream set successfully');
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [stream, showCamera]);

  // Initialize camera
  const initializeCamera = useCallback(async () => {
    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Camera is not available in your browser. Please use the upload option instead.');
        setShowCamera(false);
        return;
      }

      // Check if we're on HTTPS or localhost (required for camera access)
      const isSecure = window.location.protocol === 'https:' || 
                       window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname === '0.0.0.0';
      
      if (!isSecure) {
        setError('Camera access requires HTTPS or localhost. Please use the upload option instead, or access the app via https:// or localhost.');
        setShowCamera(false);
        return;
      }
      
      // Check permissions API if available
      if (navigator.permissions) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'camera' });
          if (permissionStatus.state === 'denied') {
            setError('Camera permission was denied. Please enable camera access in your browser settings (usually in the address bar or browser settings > Privacy > Camera) and refresh the page.');
            setShowCamera(false);
            return;
          }
        } catch (permError) {
          // Permissions API might not be supported, Continue ->
          console.log('Permissions API not available, continuing...');
        }
      }

      // Try to get camera access with fallback options
      let mediaStream;
      try {
        // First try: back camera (preferred for mobile)
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });
      } catch (backCameraError) {
        console.log('Back camera failed, trying any camera...', backCameraError);
        // Fallback: any available camera
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            video: true
          });
        } catch (anyCameraError) {
          throw anyCameraError; // Re-throw to be caught by outer catch
        }
      }

      setStream(mediaStream);
      setShowCamera(true);
      setError('');
      // Video will be set by useEffect when showCamera becomes true
    } catch (err) {
      console.error('Camera access error:', err);
      // Provide more specific error messages
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access in your browser settings or use the upload option instead.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found. Please use the upload option instead.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Camera is already in use by another application. Please close other apps using the camera or use the upload option.');
      } else if (err.name === 'OverconstrainedError') {
        setError('Camera constraints not supported. Please use the upload option instead.');
      } else {
        setError(`Camera access error: ${err.message || 'Unknown error'}. Please use the upload option instead.`);
      }
      setShowCamera(false);
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  // Capture photo from camera
  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], 'captured-photo.jpg', { type: 'image/jpeg' });
        setCapturedImage(file);
        stopCamera();
        processImage(file);
      }, 'image/jpeg', 0.9);
    }
  }, [stopCamera]);

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setCapturedImage(file);
    processImage(file);
  };

  // Process image based on active tab
  const processImage = async (file) => {
    setIsProcessing(true);
    setIsScanning(true);
    setError('');
    setProcessingProgress(0);

    try {
      if (activeTab === 'receipt') {
        await processReceipt(file);
      } else {
        await processGroceries(file);
      }
    } catch (err) {
      console.error('Processing error:', err);
      setError(formatScannerError(err, activeTab));
      setIsProcessing(false);
      setIsScanning(false);
    }
  };

  const isRateLimitError = (message) => {
    const normalized = String(message || '').toLowerCase();
    return (
      normalized.includes('429') ||
      normalized.includes('too many requests') ||
      normalized.includes('quota exceeded') ||
      normalized.includes('rate limit') ||
      normalized.includes('limit reached')
    );
  };

  const extractApiErrorMessage = (errorData, fallback) => {
    if (!errorData || typeof errorData !== 'object') return fallback;
    const candidates = [errorData.error, errorData.message, errorData.details];
    const firstMessage = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
    return firstMessage ? firstMessage.trim() : fallback;
  };

  const formatScannerError = (error, tab = activeTab) => {
    const raw = String(error?.message || '').trim();
    if (!raw) {
      return tab === 'receipt'
        ? 'Could not process receipt. Try another photo or add items manually.'
        : 'Could not recognize product. Try another photo or add it manually.';
    }

    if (isRateLimitError(raw)) {
      return tab === 'receipt'
        ? 'AI receipt scanning limit reached. Please wait a moment and try again, or add items manually.'
        : 'AI product scanning limit reached. Please wait a moment and try again, or add the item manually.';
    }

    if (raw.length > 260 || raw.includes('http') || raw.includes('{"@type"')) {
      return tab === 'receipt'
        ? 'Receipt scan failed. Please try again or add items manually.'
        : 'Product recognition failed. Please try again or add the item manually.';
    }

    return raw;
  };

  const fetchWithTimeout = async (url, options = {}, timeoutMs = 12000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal
      });
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // Process receipt using OCR
  const processReceipt = async (file) => {
    // Progress simulation
    const progressInterval = setInterval(() => {
      setProcessingProgress(prev => {
        if (prev < 90) return prev + Math.random() * 10;
        return prev;
      });
    }, 200);

    try {
      // First try multimodal receipt extraction via backend AI (better than raw OCR parsing).
      try {
        const base64ForAI = await fileToBase64(file);
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const token = localStorage.getItem('authToken');

        if (token) {
          const aiReceiptResponse = await fetch(`${API_BASE_URL}/ai/parse-receipt`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              image: base64ForAI,
              mimeType: file.type || 'image/jpeg'
            })
          });

          if (aiReceiptResponse.ok) {
            const aiReceiptData = await aiReceiptResponse.json();
            const aiItems = Array.isArray(aiReceiptData.items) ? aiReceiptData.items : [];
            const normalizedAiItems = normalizeReceiptItems(aiItems);

            const aiSectionText = String(aiReceiptData.receiptSectionText || '').trim();
            const hasStrongAiResult = normalizedAiItems.length >= 3;

            if (hasStrongAiResult) {
              clearInterval(progressInterval);
              setProcessingProgress(100);
              setExtractedOcrText(aiSectionText);
              setProducts(normalizedAiItems);
              setIsReviewMode(true);
              return;
            }

            // If AI couldn't return structured items, try parsing the extracted receipt section text.
            if (aiSectionText) {
              const fallbackItemsFromAiSection = await parseReceiptProducts(aiSectionText);
              if (fallbackItemsFromAiSection.length >= 2) {
                clearInterval(progressInterval);
                setProcessingProgress(100);
                setExtractedOcrText(aiSectionText);
                setProducts(
                  fallbackItemsFromAiSection.map((item) => ({
                    name: item.name,
                    quantity: item.quantity || '1',
                    location: 'pantry',
                    expiryDate: ''
                  }))
                );
                setIsReviewMode(true);
                return;
              }
            }
          } else if (aiReceiptResponse.status === 429) {
            console.warn('AI receipt parsing is rate-limited, continuing with OCR fallback.');
          }
        }
      } catch (aiReceiptError) {
        console.warn('AI receipt parsing failed, falling back to OCR:', aiReceiptError);
      }

      // OCR pipeline: try OCR.space across multiple image variants before Tesseract fallback.
      const imageCandidates = await buildReceiptOcrCandidates(file).catch((preprocessError) => {
        console.warn('Receipt image preprocessing failed, using original image only:', preprocessError);
        return [{ label: 'original', file }];
      });
      const languages = ['lav', 'eng', 'auto'];
      const engines = ['2', '1'];
      let extractedText = '';
      let success = false;

      for (const candidate of imageCandidates) {
        if (success) break;
        for (const lang of languages) {
          if (success) break;
          for (const engine of engines) {
            try {
              const formData = new FormData();
              formData.append('file', candidate.file);
              formData.append('language', lang);
              formData.append('isOverlayRequired', 'false');
              formData.append('apikey', 'K87637863288957');
              formData.append('OCREngine', engine);
              formData.append('detectOrientation', 'true');
              formData.append('scale', 'true');

              const response = await fetchWithTimeout('https://api.ocr.space/parse/image', {
                method: 'POST',
                body: formData
              }, 8000);

              const result = await response.json();

              if (!result.IsErroredOnProcessing && result.ParsedResults && result.ParsedResults.length > 0) {
                const mergedText = result.ParsedResults
                  .map((parsed) => String(parsed?.ParsedText || '').trim())
                  .filter(Boolean)
                  .join('\n');

                if (mergedText.length > extractedText.length) {
                  extractedText = mergedText;
                }

                if (isUsefulReceiptText(mergedText)) {
                  success = true;
                  break;
                }
              }
            } catch (ocrAttemptError) {
              console.log(`OCR.space failed for ${candidate.label}/${lang}/engine-${engine}, trying next...`);
            }
          }
        }
      }

      clearInterval(progressInterval);
      setProcessingProgress(100);

      if (!success || !extractedText) {
        // Fallback to Tesseract.js
        console.log('OCR.space failed, trying Tesseract.js fallback...');
        setProcessingProgress(20);

        const worker = await createWorker('eng+lav');
        const tesseractCandidates = imageCandidates.length > 0 ? imageCandidates : [{ label: 'original', file }];
        const pageSegModes = ['4', '6'];

        try {
          for (const candidate of tesseractCandidates) {
            for (const psm of pageSegModes) {
              await worker.setParameters({
                tessedit_pageseg_mode: psm,
                tessedit_ocr_engine_mode: '1',
                preserve_interword_spaces: '1'
              });

              const { data: { text } } = await worker.recognize(candidate.file);
              const recognizedText = String(text || '').trim();
              if (recognizedText.length > extractedText.length) {
                extractedText = recognizedText;
              }
              if (isUsefulReceiptText(recognizedText)) {
                success = true;
                break;
              }
            }
            if (success) break;
          }
        } finally {
          await worker.terminate();
        }

        setProcessingProgress(100);
      }

      // Store extracted text for debugging (preserve formatting with <pre>)
      setExtractedOcrText(extractedText);
      
      // Parse products from extracted text locally first.
      let extractedProducts = await parseReceiptProducts(extractedText);

      // If local heuristics still struggle, use the OCR text with AI as a second pass.
      if (extractedProducts.length < 2 && extractedText) {
        try {
          const aiProductsFromText = await parseReceiptTextWithAI(extractedText);
          if (aiProductsFromText.length > extractedProducts.length) {
            extractedProducts = aiProductsFromText;
          }
        } catch (receiptTextAiError) {
          console.warn('AI OCR-text receipt parsing failed, keeping local parser result:', receiptTextAiError);
        }
      }
      
      if (extractedProducts.length === 0) {
        // If no products found, still show review mode with OCR text for debugging
        console.log('No products extracted from receipt. OCR text:', extractedText);
      }
      
      setProducts(extractedProducts);
      setIsReviewMode(true);
    } catch (err) {
      clearInterval(progressInterval);
      throw new Error(`Receipt processing failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
      setIsScanning(false);
    }
  };

  // Process groceries using image recognition
  const processGroceries = async (file) => {
    // Progress simulation
    const progressInterval = setInterval(() => {
      setProcessingProgress(prev => {
        if (prev < 90) return prev + Math.random() * 10;
        return prev;
      });
    }, 200);

    try {
      const imageCandidates = await buildVisionImageCandidates(file).catch((preprocessError) => {
        console.warn('Vision image preprocessing failed, using original image only:', preprocessError);
        return [{ label: 'original', file }];
      });

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      let result = null;
      let lastError = null;

      for (const candidate of imageCandidates) {
        let base64 = await fileToBase64(candidate.file);
        if (base64.includes(',')) {
          base64 = base64.split(',')[1];
        }

        console.log('[INFO] Calling product recognition API with image variant:', candidate.label);
        const response = await fetch(`${API_BASE_URL}/ai/recognize-product`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            image: base64,
            mimeType: candidate.file.type || file.type || 'image/jpeg'
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            error: `Server error: ${response.status} ${response.statusText}`
          }));
          console.error('[ERROR] API Error:', errorData);
          const fallbackError = response.status === 429
            ? 'AI product scanning limit reached. Please wait a moment and try again.'
            : `Failed to recognize product (${response.status})`;
          lastError = new Error(extractApiErrorMessage(errorData, fallbackError));

          if (response.status === 429) {
            throw lastError;
          }

          continue;
        }

        const candidateResult = await response.json();
        console.log('[OK] Product recognition result:', candidate.label, candidateResult);

        const candidateProducts = Array.isArray(candidateResult.products) ? candidateResult.products : [];
        const hasRecognizedProduct =
          candidateProducts.length > 0 ||
          (candidateResult.productName && candidateResult.productName !== 'Unknown Product');

        if (hasRecognizedProduct) {
          result = candidateResult;
          break;
        }
      }

      clearInterval(progressInterval);
      setProcessingProgress(100);

      if (!result) {
        throw lastError || new Error('Could not identify the product. Please try a clearer image or add the item manually.');
      }

      const resultProducts = Array.isArray(result.products) ? result.products : [];

      // Check if we got valid product data
      if (
        resultProducts.length === 0 &&
        (!result.productName || result.productName === 'Unknown Product')
      ) {
        throw new Error('Could not identify the product. Please try a clearer image or add the item manually.');
      }

      const toExpiryDate = (expiryText) => {
        if (!expiryText) return '';
        const expiryMatch = expiryText.match(/(\d+)\s*(day|days|month|months|week|weeks)/i);
        if (!expiryMatch) return '';

        const amount = parseInt(expiryMatch[1], 10);
        const unit = expiryMatch[2].toLowerCase();
        const date = new Date();
        if (unit.includes('day')) {
          date.setDate(date.getDate() + amount);
        } else if (unit.includes('week')) {
          date.setDate(date.getDate() + (amount * 7));
        } else if (unit.includes('month')) {
          date.setMonth(date.getMonth() + amount);
        }
        return date.toISOString().split('T')[0];
      };

      const mappedProducts = (resultProducts.length > 0 ? resultProducts : [result])
        .map((product) => ({
          name: product.productName || product.name || '',
          quantity: product.quantity || '1',
          category: product.category || '',
          expiryDate: toExpiryDate(product.expiryDate),
          location: product.location === 'fridge' ? 'fridge' : 'pantry'
        }))
        .filter((product) => product.name && product.name !== 'Unknown Product');

      if (mappedProducts.length === 0) {
        throw new Error('Could not identify the product. Please try a clearer image or add the item manually.');
      }

      setProducts(mappedProducts);
      setIsReviewMode(true);
    } catch (err) {
      clearInterval(progressInterval);
      console.error('Product recognition error:', err);
      // Provide user-friendly error message
      const errorMessage = formatScannerError(err, 'groceries');
      throw new Error(errorMessage);
    } finally {
      setIsProcessing(false);
      setIsScanning(false);
    }
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const normalizeReceiptItems = (items) => {
    const blockedNamePatterns = [
      /\bcena\b/i,
      /\bgala\s*cena\b/i,
      /\batl(?:aide|aides|\.?)\b/i,
      /\bpvn\b/i,
      /\bsumma\b/i,
      /\bkopa\b/i,
      /\bmaksaj(?:um|u)u?\b/i,
      /\bapmaksa\b/i,
      /\bkarte\b/i,
      /\bvisa\b/i,
      /\bkvits\b/i,
      /\bdokumentu\b/i,
      /\bkonta\b/i,
      /\bmans\s*rimi\b/i,
      /\bietaupijums\b/i,
      /\brimi\s*hyper\b/i,
      /\bkase\b/i,
      /\beur\s*\/\s*[a-z]+\b/i,
      /\b\d+[.,]\d{2}\s*eur\s*\/\s*[a-z]+\b/i,
      /\bwalmart\b/i,
      /\bsave money\b/i,
      /\bsubtotal\b/i,
      /\btotal\b/i,
      /\btax\b/i,
      /\bdebit\b/i,
      /\bcredit\b/i,
      /\bpayment\b/i,
      /\bchange due\b/i,
      /\bpay from\b/i,
      /\bnetwork\b/i,
      /\baccount\b/i,
      /\bitems sold\b/i,
      /\btransaction\b/i,
      /\breceipt\b/i,
      /\bthank you\b/i,
      /\bstore\b/i,
      /\bmanager\b/i,
      /\bterminal\b/i,
      /\bref\b/i,
      /\btc#\b/i,
      /\bst#\b/i,
      /\bop#\b/i,
      /\bte#\b/i,
      /\btr#\b/i
    ];

    const isValidReceiptName = (name) => {
      const normalized = String(name || '')
        .replace(/[|_~`^]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      if (normalized.length < 3 || normalized.length > 100) return false;
      if (!/[A-Za-z\u00C0-\u024F\u0100-\u017F]{3,}/.test(normalized)) return false;
      const lettersOnly = normalized.replace(/[^A-Za-z\u00C0-\u024F\u0100-\u017F]/g, '');
      if (lettersOnly.length < 3) return false;
      if (/^\s*[\d.,xX]+\s*$/.test(normalized)) return false;
      if (/^\s*\d/.test(normalized)) return false;
      if (/^\s*\*+\s*\d/.test(normalized)) return false;
      if (/\b\d+\s*[xX]\b/.test(normalized)) return false;
      if (/\d+[,.]\d{2}\s*(eur|euro|a)?$/i.test(normalized)) return false;
      if (/\b(kg|g|ug|mg|l|ml)\s+\d+[.,]\d{2}\s*eur/i.test(normalized)) return false;
      if (/^\d+([,.]\d+)?$/.test(normalized)) return false;
      const digitCount = (normalized.match(/\d/g) || []).length;
      if (digitCount > lettersOnly.length) return false;
      if (blockedNamePatterns.some((pattern) => pattern.test(normalized))) return false;
      return true;
    };

    return (Array.isArray(items) ? items : [])
      .map((item) => ({
        name: String(item?.name || '').trim(),
        quantity: String(item?.quantity || '1').trim() || '1',
        location: 'pantry',
        expiryDate: ''
      }))
      .filter((item) => isValidReceiptName(item.name))
      .slice(0, 40);
  };

  const isUsefulReceiptText = (text) => {
    const normalized = String(text || '').trim();
    if (normalized.length < 30) return false;
    if (!/\d+[,.]\d{2}/.test(normalized)) return false;
    return /[A-Za-z\u00C0-\u024F\u0100-\u017F]{3,}/.test(normalized);
  };

  const buildReceiptOcrCandidates = async (file) => {
    const candidates = [{ label: 'original', file }];

    const sourceImage = await fileToImage(file);
    const maxWidth = 2200;
    const scale = sourceImage.width > maxWidth ? maxWidth / sourceImage.width : 1;
    const width = Math.max(1, Math.round(sourceImage.width * scale));
    const height = Math.max(1, Math.round(sourceImage.height * scale));

    const baseCanvas = document.createElement('canvas');
    baseCanvas.width = width;
    baseCanvas.height = height;
    const baseCtx = baseCanvas.getContext('2d', { willReadFrequently: true });
    if (!baseCtx) return candidates;
    baseCtx.drawImage(sourceImage, 0, 0, width, height);

    const grayCanvas = document.createElement('canvas');
    grayCanvas.width = width;
    grayCanvas.height = height;
    const grayCtx = grayCanvas.getContext('2d', { willReadFrequently: true });
    if (grayCtx) {
      grayCtx.drawImage(baseCanvas, 0, 0);
      const imageData = grayCtx.getImageData(0, 0, width, height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
        const boosted = Math.max(0, Math.min(255, (gray - 128) * 1.45 + 128));
        data[i] = boosted;
        data[i + 1] = boosted;
        data[i + 2] = boosted;
      }
      grayCtx.putImageData(imageData, 0, 0);
      const enhancedFile = await canvasToJpegFile(grayCanvas, `${stripFileExtension(file.name)}-enhanced.jpg`);
      candidates.push({ label: 'enhanced', file: enhancedFile });
    }

    const bwCanvas = document.createElement('canvas');
    bwCanvas.width = width;
    bwCanvas.height = height;
    const bwCtx = bwCanvas.getContext('2d', { willReadFrequently: true });
    if (bwCtx) {
      bwCtx.drawImage(baseCanvas, 0, 0);
      const imageData = bwCtx.getImageData(0, 0, width, height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
        const threshold = gray > 165 ? 255 : 0;
        data[i] = threshold;
        data[i + 1] = threshold;
        data[i + 2] = threshold;
      }
      bwCtx.putImageData(imageData, 0, 0);
      const bwFile = await canvasToJpegFile(bwCanvas, `${stripFileExtension(file.name)}-bw.jpg`);
      candidates.push({ label: 'binarized', file: bwFile });
    }

    return candidates;
  };

  const buildVisionImageCandidates = async (file) => {
    const sourceImage = await fileToImage(file);
    const maxWidth = 1800;
    const scale = sourceImage.width > maxWidth ? maxWidth / sourceImage.width : 1;
    const width = Math.max(1, Math.round(sourceImage.width * scale));
    const height = Math.max(1, Math.round(sourceImage.height * scale));

    const baseCanvas = document.createElement('canvas');
    baseCanvas.width = width;
    baseCanvas.height = height;
    const baseCtx = baseCanvas.getContext('2d', { willReadFrequently: true });
    if (!baseCtx) return [{ label: 'original', file }];

    baseCtx.drawImage(sourceImage, 0, 0, width, height);

    const candidates = [{ label: 'original', file }];
    const resizedFile = await canvasToJpegFile(baseCanvas, `${stripFileExtension(file.name)}-vision.jpg`);
    candidates.push({ label: 'resized', file: resizedFile });

    const enhancedCanvas = document.createElement('canvas');
    enhancedCanvas.width = width;
    enhancedCanvas.height = height;
    const enhancedCtx = enhancedCanvas.getContext('2d', { willReadFrequently: true });

    if (enhancedCtx) {
      enhancedCtx.drawImage(baseCanvas, 0, 0);
      const imageData = enhancedCtx.getImageData(0, 0, width, height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.max(0, Math.min(255, (data[i] - 128) * 1.12 + 132));
        data[i + 1] = Math.max(0, Math.min(255, (data[i + 1] - 128) * 1.08 + 128));
        data[i + 2] = Math.max(0, Math.min(255, (data[i + 2] - 128) * 1.08 + 128));
      }

      enhancedCtx.putImageData(imageData, 0, 0);
      const enhancedFile = await canvasToJpegFile(enhancedCanvas, `${stripFileExtension(file.name)}-vision-enhanced.jpg`);
      candidates.push({ label: 'enhanced', file: enhancedFile });
    }

    return candidates;
  };

  const fileToImage = (file) =>
    new Promise((resolve, reject) => {
      const imageUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(imageUrl);
        resolve(image);
      };

      image.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error('Failed to load image for OCR preprocessing'));
      };

      image.src = imageUrl;
    });

  const canvasToJpegFile = (canvas, name) =>
    new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to generate preprocessed image'));
            return;
          }
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        0.95
      );
    });

  const stripFileExtension = (name) => String(name || 'receipt').replace(/\.[^/.]+$/, '');

  const parseReceiptTextWithAI = async (rawText) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('authToken');
    if (!token) return [];

    const response = await fetch(`${API_BASE_URL}/ai/parse-receipt-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        text: rawText
      })
    });

    if (!response.ok) {
      return [];
    }

    const payload = await response.json();
    return normalizeReceiptItems(payload.items || []);
  };

  // Parse grocery/general retail receipt products, resilient to Walmart-style OCR noise.
  const parseReceiptProducts = async (text) => {
    const sanitizeReceiptProductName = (value) =>
      String(value || '')
        .replace(/[|_~`^]/g, ' ')
        .replace(/\s+\d+[,.]\d{2}\s*(EUR|A|X)?$/i, '')
        .replace(/\b(?:x|шт)\b$/i, '')
        .replace(/\s{2,}/g, ' ')
        .replace(/^[*#\s-]+/, '')
        .trim();

    const isClearlyNonReceiptLine = (line) => {
      const normalized = String(line || '').trim();
      if (!normalized) return true;

      // Backend/SQL/debug noise should never be treated as receipt products.
      if (/^Executing\s*\(/i.test(normalized)) return true;
      if (/^(SELECT|INSERT|UPDATE|DELETE|FROM|WHERE|LEFT OUTER JOIN|RIGHT OUTER JOIN|INNER JOIN|ORDER BY|LIMIT)\b/i.test(normalized)) return true;
      if (/^\W*"\w+" AS "\w+\.\w+"/i.test(normalized)) return true;
      if (/\bsequelize\b|\btransaction\b|\breturning\b|\bmodel\b|\bgooglegenerativeai\b/i.test(normalized)) return true;
      if (/^[\s"'`,.()=:$[\]{}<>_-]+$/.test(normalized)) return true;
      if (/^\d+\s+\d+\s*-\s*\d+$/i.test(normalized)) return true;
      if (/^(walmart|save money|eft debit|pay from|approval|purchase|account|network|items sold|teacher appreciation|transaction|terminal|change due|subtotal|total|tax|cash|debit|credit|ref\b|tc#|st#|op#|te#|tr#|manager|plano|ohio|tx\s*\d{5})/i.test(normalized)) return true;

      return false;
    };

    const normalizedLines = text
      .split('\n')
      .map((line) => line.replace(/\s+/g, ' ').trim())
      .filter((line) => line.length > 0)
      .filter((line) => !isClearlyNonReceiptLine(line));

    const lines = normalizedLines;

    const products = [];
    const seen = new Set();

    const skipRow = (line) =>
      /^(subtotal|total|tax|vat|discount|savings|payment|paid|change|cash|credit|debit|card|card no|auth|approval|terminal|store|address|tel|phone|cashier|register|receipt|invoice|order|transaction|date|time|items sold|balance|points|loyalty|thank you|coupon|promo|manager|operator|reference|ref|network|account|customer|client|sum|amount|price\b|unit price|grand total|service fee|tip|walmart|save money|teacher appreciation|purchase|primary|sale|dept|plano|ohio|tx \d{5})/i.test(line);

    const isStandalonePriceLine = (line) =>
      /^[$]?\d{1,3}[,.]\d{2}\s*[xX]?$/i.test(line) ||
      /^[$]?\.\d{2}\s*[xX]?$/i.test(line) ||
      /^\d{1,3}\.\s*\d{2}\s*[xX]?$/i.test(line);

    const isInlinePriceProductLine = (line) =>
      /[A-Za-z\u00C0-\u024F\u0100-\u017F]{2,}/.test(line) &&
      /\d+[,.]\d{2}\s*[xX]?\s*$/i.test(line) &&
      !skipRow(line);

    const isMostlyNumericMetaLine = (line) =>
      /^\d{4,14}(\s+\d{2,5})?\s*[xX]?$/.test(line) ||
      /^\d{5,}\s+[A-Z]{0,2}$/.test(line);

    const looksLikeProductText = (line) => {
      if (skipRow(line)) return false;
      if (!/[A-Za-z\u00C0-\u024F\u0100-\u017F]{2,}/.test(line)) return false;
      if (isMostlyNumericMetaLine(line)) return false;
      return true;
    };

    const isValidParsedName = (name) => {
      const normalized = sanitizeReceiptProductName(name);

      if (normalized.length < 3 || normalized.length > 100) return false;
      if (!/[A-Za-z\u00C0-\u024F\u0100-\u017F]{2,}/.test(normalized)) return false;
      const lettersOnly = normalized.replace(/[^A-Za-z\u00C0-\u024F\u0100-\u017F]/g, '');
      if (lettersOnly.length < 3) return false;
      if (/^\s*[\d.,xX]+\s*$/.test(normalized)) return false;
      if (/^\s*\d/.test(normalized) && !/^\d+\s*(pk|pack|pc|pcs|ct)\b/i.test(normalized)) return false;
      if (/^\s*\*+\s*\d/.test(normalized)) return false;
      if (/\b\d+\s*[xX]\b/.test(normalized)) return false;
      if (/\d+[,.]\d{2}\s*(eur|euro|a)?$/i.test(normalized)) return false;
      if (/\beur\s*\/\s*[a-z]+\b/i.test(normalized)) return false;
      if (/\b(kg|g|ug|mg|l|ml)\s+\d+[.,]\d{2}\s*eur/i.test(normalized)) return false;
      const digitCount = (normalized.match(/\d/g) || []).length;
      if (digitCount > lettersOnly.length) return false;
      if (skipRow(normalized)) return false;
      return true;
    };

    const cleanProductName = (line) =>
      sanitizeReceiptProductName(
        line
          .replace(/\s+[A-Z]$/, '')
          .replace(/\s+\d+[,.]\d{2}\s*[xX]?$/i, '')
      );

    const pushProduct = (nameParts, quantity = '1', originalLine = '') => {
      const productName = cleanProductName(
        nameParts
          .filter(Boolean)
          .filter((part) => !isMostlyNumericMetaLine(part))
          .join(' ')
          .replace(/\b(?:COM|DEPT|TRE|X)\b/gi, ' ')
      );

      if (!isValidParsedName(productName)) return;

      const dedupeKey = `${productName.toLowerCase()}|${quantity}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      products.push({ name: productName, quantity, originalLine });
    };

    let currentParts = [];
    let currentOriginal = [];

    const flushCurrent = () => {
      if (currentParts.length === 0) return;
      pushProduct(currentParts, '1', currentOriginal.join(' | '));
      currentParts = [];
      currentOriginal = [];
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (skipRow(line)) {
        flushCurrent();
        continue;
      }

      if (isInlinePriceProductLine(line)) {
        flushCurrent();
        pushProduct([line], '1', line);
        continue;
      }

      if (isStandalonePriceLine(line)) {
        flushCurrent();
        continue;
      }

      if (isMostlyNumericMetaLine(line)) {
        if (currentParts.length > 0) {
          currentOriginal.push(line);
        }
        continue;
      }

      if (!looksLikeProductText(line)) {
        flushCurrent();
        continue;
      }

      const nextLine = lines[i + 1] || '';
      const startsNewGroup =
        currentParts.length > 0 &&
        !isMostlyNumericMetaLine(nextLine) &&
        looksLikeProductText(line) &&
        currentParts.length >= 2;

      if (startsNewGroup) {
        flushCurrent();
      }

      currentParts.push(line);
      currentOriginal.push(line);

      if (currentParts.length >= 4) {
        flushCurrent();
      }
    }

    flushCurrent();

    // Fallback: take standalone product-like lines even without prices.
    if (products.length < 2) {
      lines.forEach((line) => {
        if (!looksLikeProductText(line)) return;
        if (line.length < 3 || line.length > 40) return;
        pushProduct([line], '1', line);
      });
    }

    return products;
  };

  // Validate product with AI
  const validateProductWithAI = async (productName) => {
    try {
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          message: `Analyze this text from a grocery receipt: "${productName}". Determine if it's a FOOD PRODUCT. Answer only YES or NO.`,
          conversationHistory: []
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        const aiResponse = result.response.toLowerCase();
        return aiResponse.includes('yes') && !aiResponse.includes('no');
      }
    } catch (error) {
      console.error('AI validation failed:', error);
    }
    
    // Fallback: simple heuristics
    const productIndicators = [
      /^[A-Za-z]/,
      /[A-Za-z]{3,}/,
      /(kg|g|ml|l|pcs|pack|paka)/i
    ];
    
    const hasProductIndicators = productIndicators.some(pattern => pattern.test(productName));
    return hasProductIndicators && productName.length > 3;
  };

  // Handle product edits
  const handleProductNameChange = (index, newName) => {
    const updatedProducts = [...products];
    updatedProducts[index].name = newName;
    setProducts(updatedProducts);
  };

  const handleQuantityChange = (index, newQuantity) => {
    const updatedProducts = [...products];
    updatedProducts[index].quantity = newQuantity;
    setProducts(updatedProducts);
  };

  const handleLocationChange = (index, location) => {
    const updatedProducts = [...products];
    updatedProducts[index].location = location;
    setProducts(updatedProducts);
  };

  const removeProduct = (index) => {
    const updatedProducts = products.filter((_, i) => i !== index);
    setProducts(updatedProducts);
  };

  const handleAddManualItem = () => {
    setProducts([
      {
        name: '',
        quantity: '1',
        location: 'pantry',
        expiryDate: '',
        description: ''
      }
    ]);
    setError('');
    setIsReviewMode(true);
  };

  const handleEditProduct = (index) => {
    const input = productNameInputRefs.current[index];
    if (!input) return;
    input.focus();
    if (typeof input.select === 'function') {
      input.select();
    }
  };

  // Add products to pantry
  const handleAddToPantry = () => {
    const validProducts = products.filter(p => p.name.trim().length > 0);
    onProductsExtracted(validProducts);
    onClose();
  };

  // Calculate expiry text from date
  const calculateExpiryText = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Expires today';
    if (diffDays === 1) return 'Expires in 1 day';
    if (diffDays < 7) return `Expires in ${diffDays} days`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `Expires in ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
    }
    const months = Math.floor(diffDays / 30);
    return `Expires in ${months} ${months === 1 ? 'month' : 'months'}`;
  };

  // Reset scanner
  const resetScanner = () => {
    setProducts([]);
    setIsReviewMode(false);
    setError('');
    setProcessingProgress(0);
    setCapturedImage(null);
    setExtractedOcrText(''); // Clear OCR text
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    if (isProcessing || isScanning) return;
    setActiveTab(tab);
    resetScanner();
    if (showCamera) {
      stopCamera();
    }
    // Reset camera state when switching tabs
    setShowCamera(false);
    setCapturedImage(null);
  };

  // Loading screen component
  const LoadingScreen = () => (
    <div className="item-scanner-loading">
      <div className="loading-content">
        <div className="loading-spinner">
          <div className="spinner-circle"></div>
        </div>
        <div className="loading-text">
          <p className="loading-line1">Finding foods,</p>
          <p className="loading-line2">categories and expiry dates.</p>
          <p className="loading-time">(Usually 3 - 12 seconds)</p>
        </div>
        <div className="loading-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${processingProgress}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );

  // Review/Edit screen
  if (isReviewMode) {
    return (
      <div className="item-scanner-overlay">
        <div className="item-scanner-container">
          <div className="item-scanner-header">
            <button className="back-btn-icon" onClick={resetScanner} title="Back">{"<"}</button>
            <h2>Preview</h2>
            <div className="item-scanner-subheader">
              <span className="item-scanner-category">PRODUCT REVIEW</span>
              <HelpDisclosure title="Review help" items={scannerReviewHelp} />
            </div>
            <button className="close-btn-icon" onClick={onClose} title="Close">x</button>
          </div>
          
          <div className="item-scanner-content">
            {/* Show receipt text used for extraction */}
            {extractedOcrText && (
              <div className="ocr-debug-section">
                <h3 style={{ marginBottom: '10px', color: '#374151', fontSize: '16px', fontWeight: '600' }}>
                  Receipt Text Used For Extraction:
                </h3>
                <pre
                  style={{
                    width: '100%',
                    minHeight: '150px',
                    maxHeight: '400px',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    backgroundColor: '#f9fafb',
                    color: '#1f2937',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    marginBottom: '20px',
                    margin: '0 0 20px 0'
                  }}
                >
                  {extractedOcrText}
                </pre>
                {products.length === 0 && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fef3c7',
                    border: '1px solid #fbbf24',
                    borderRadius: '8px',
                    color: '#92400e',
                    marginBottom: '20px'
                  }}>
                    No products were extracted from the OCR text. Please check the text above and manually add items if needed.
                  </div>
                )}
              </div>
            )}
            
            <div className="products-preview-list">
              {products.length === 0 ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: '#6b7280'
                }}>
                  <p style={{ fontSize: '16px', marginBottom: '10px' }}>No products found</p>
                  <p style={{ fontSize: '14px' }}>Try uploading a clearer image or add items manually</p>
                </div>
              ) : (
                products.map((product, index) => (
                <div key={index} className="product-preview-card">
                  <div className="product-preview-left">
                    <div className="product-info">
                      <input
                        type="text"
                        value={product.name}
                        onChange={(e) => handleProductNameChange(index, e.target.value)}
                        className="product-name-input-preview"
                        placeholder="Product name"
                        ref={(element) => {
                          productNameInputRefs.current[index] = element;
                        }}
                      />
                      <input
                        type="text"
                        value={product.quantity || ''}
                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                        className="product-name-input-preview"
                        placeholder="Quantity (e.g. 1, 500g, 1L)"
                      />
                      <div className="product-expiry-display">
                        <input
                          type="date"
                          value={product.expiryDate || ''}
                          onChange={(e) => {
                            const updated = [...products];
                            updated[index].expiryDate = e.target.value;
                            setProducts(updated);
                          }}
                          className="product-expiry-input"
                          placeholder="Set expiry date"
                          min={new Date().toISOString().split('T')[0]}
                        />
                        {product.expiryDate && (
                          <span className="expiry-text">{calculateExpiryText(product.expiryDate)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="product-actions">
                    <button 
                      className="edit-btn"
                      onClick={() => handleEditProduct(index)}
                      title="Edit"
                    >
                      Edit
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => removeProduct(index)}
                      title="Delete"
                    >
                      Delete
                    </button>
                    <div className="location-toggle-group">
                      <button 
                        className={`location-btn ${product.location === 'pantry' ? 'active' : ''}`}
                        onClick={() => handleLocationChange(index, 'pantry')}
                      >
                        My pantry
                      </button>
                      <button 
                        className={`location-btn ${product.location === 'fridge' ? 'active' : ''}`}
                        onClick={() => handleLocationChange(index, 'fridge')}
                      >
                        My fridge
                      </button>
                    </div>
                  </div>
                </div>
                ))
              )}
            </div>
          </div>
          
          <div className="item-scanner-footer">
            <button 
              onClick={handleAddToPantry} 
              className="add-all-btn"
              disabled={products.length === 0}
            >
              Add all {products.length} item(s)
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main scanner screen
  return (
    <div className="item-scanner-overlay">
      <div className="item-scanner-container">
        <div className="item-scanner-header">
          <button className="back-btn-icon" onClick={onClose} title="Back">{"<"}</button>
          <h2>Scan Item</h2>
          <div className="item-scanner-subheader">
            <span className="item-scanner-category">ITEM SCANNING</span>
            <HelpDisclosure title="Scanner help" items={scannerHelp} />
          </div>
          <button className="close-btn-icon" onClick={onClose} title="Close">x</button>
        </div>

        {/* Tab Selector */}
        <div className="tab-selector">
          <button
            className={`tab-btn ${activeTab === 'receipt' ? 'active' : ''}`}
            onClick={() => handleTabChange('receipt')}
            disabled={isProcessing || isScanning}
          >
            Receipt
          </button>
          <button
            className={`tab-btn ${activeTab === 'groceries' ? 'active' : ''}`}
            onClick={() => handleTabChange('groceries')}
            disabled={isProcessing || isScanning}
          >
            Groceries
          </button>
          <button
            className={`tab-btn ${activeTab === 'barcode' ? 'active' : ''}`}
            onClick={() => handleTabChange('barcode')}
            disabled={isProcessing || isScanning}
          >
            Barcode
          </button>
        </div>

        <div className="item-scanner-content">
          {activeTab === 'barcode' ? (
            <BarcodeScannerInline
              onScan={(productData) => {
                // Product data is already in the right format
                setProducts([productData]);
                setIsReviewMode(true);
              }}
              onClose={() => {
                // Switch back to receipt tab when barcode scanner closes
                setActiveTab('receipt');
              }}
            />
          ) : isScanning || isProcessing ? (
            <LoadingScreen />
          ) : showCamera ? (
            <div className="camera-view">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="camera-video"
                style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <div className="camera-controls">
                <button 
                  className="upload-btn-camera"
                  onClick={() => {
                    stopCamera();
                    fileInputRef.current?.click();
                  }}
                >
                  Import Photo
                </button>
                <button 
                  className="capture-btn"
                  onClick={capturePhoto}
                >
                  <span className="camera-icon" aria-hidden="true">{'\uD83D\uDCF7'}</span>
                </button>
                <HelpDisclosure title="Camera help" items={scannerHelp} compact />
              </div>
              {capturedImage && (
                <div className="captured-preview">
                  <img src={URL.createObjectURL(capturedImage)} alt="Captured" />
                  <button 
                    className="remove-capture-btn"
                    onClick={() => {
                      setCapturedImage(null);
                      initializeCamera();
                    }}
                  >
                    x
                  </button>
                  <button 
                    className="continue-btn"
                    onClick={() => processImage(capturedImage)}
                  >
                    Continue ->
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="upload-section">
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              <div className="upload-main">
                <button 
                  className="camera-primary-btn"
                  onClick={initializeCamera}
                  disabled={isProcessing || isScanning}
                >
                  <span className="camera-icon-large" aria-hidden="true">{'\uD83D\uDCF7'}</span>
                  <span>Take Photo</span>
                </button>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  ref={fileInputRef}
                  id="file-upload"
                  style={{ display: 'none' }}
                />
                <button 
                  className="upload-secondary-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isProcessing || isScanning}
                >
                  Import Photo
                </button>
                {(activeTab === 'groceries' || activeTab === 'receipt') && (
                  <button
                    className="upload-secondary-btn manual-add-btn"
                    onClick={handleAddManualItem}
                    disabled={isProcessing || isScanning}
                  >
                    Add Manually
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemScanner;



